import { getSafeRedirectUrl } from '@talleflow/safe-redirect';

import {
  buildAdminTemplateEditorPath,
  isAdminTemplateEditorRoute,
} from '@/utils/adminTemplateAuthoringPath';

import type { NavigateFunction } from 'react-router-dom';

export type UnlayerEditorOrigin = 'documentsList' | 'projectDocuments' | 'contactDocuments';
export type UnlayerEditorEntryRouteKind =
  'projectsDocuments' | 'documentsList' | 'contactsDocuments' | 'other';

export interface UnlayerEditorEntryRouteContext {
  readonly kind: UnlayerEditorEntryRouteKind;
  readonly path: string;
  readonly stateTab: number | null;
}

function parseEditorOrigin(
  searchParams: URLSearchParams | Readonly<URLSearchParams>
): UnlayerEditorOrigin | null {
  const origin = searchParams.get('origin');
  if (
    origin === 'documentsList' ||
    origin === 'projectDocuments' ||
    origin === 'contactDocuments'
  ) {
    return origin;
  }
  return null;
}

export interface UnlayerEditorNavigationParams {
  readonly origin: UnlayerEditorOrigin | null;
  readonly entryRoute: UnlayerEditorEntryRouteContext | null;
  readonly projectId: string | null;
  readonly projectName: string | null;
  readonly linkedContactId: string | null;
  readonly linkedContactName: string | null;
  readonly linkedContactEmail: string | null;
  readonly contactId: string | null;
  readonly contactName: string | null;
  readonly returnTo: string | null;
  readonly campaignContext: boolean;
  readonly emailTemplateContext: boolean;
  /** Additive onboarding metadata — does not change editor behaviour. */
  readonly proposalContext: boolean;
  /** Additive onboarding metadata for the brochure Packages-block explainer. */
  readonly packagesBlockContext: boolean;
  /** Additive onboarding metadata for the invoice-block explainer. */
  readonly invoiceBlockContext: boolean;
  readonly resendParam: boolean;
  readonly sendParam: boolean;
  readonly resendContactId: string | null;
  readonly sendContactId: string | null;
  readonly shareStep: boolean;
}

function parseEntryRouteKind(
  searchParams: URLSearchParams | Readonly<URLSearchParams>
): UnlayerEditorEntryRouteKind | null {
  const kind = searchParams.get('entryRouteKind');
  if (
    kind === 'projectsDocuments' ||
    kind === 'documentsList' ||
    kind === 'contactsDocuments' ||
    kind === 'other'
  ) {
    return kind;
  }
  return null;
}

function parseEntryRoutePath(
  searchParams: URLSearchParams | Readonly<URLSearchParams>
): string | null {
  const path = searchParams.get('entryRoutePath');
  if (!path || !path.startsWith('/') || path.startsWith('//')) {
    return null;
  }
  return path;
}

function parseEntryRouteStateTab(
  searchParams: URLSearchParams | Readonly<URLSearchParams>
): number | null {
  const raw = searchParams.get('entryRouteStateTab');
  if (!raw) return null;
  const parsed = Number(raw);
  if (!Number.isInteger(parsed)) return null;
  return parsed;
}

function parseEntryRouteContext(
  searchParams: URLSearchParams | Readonly<URLSearchParams>
): UnlayerEditorEntryRouteContext | null {
  const kind = parseEntryRouteKind(searchParams);
  const path = parseEntryRoutePath(searchParams);
  if (!kind || !path) {
    return null;
  }
  return {
    kind,
    path,
    stateTab: parseEntryRouteStateTab(searchParams),
  };
}

function cloneParams(input: URLSearchParams | Readonly<URLSearchParams>): URLSearchParams {
  return new URLSearchParams(input);
}

/** Validate a raw `returnTo` query value (open-redirect safe). */
function safeReturnToParam(raw: string | null, fallback: string): string | null {
  return raw ? getSafeRedirectUrl(raw, fallback) : null;
}

export function readUnlayerNavigationParams(
  searchParams: URLSearchParams | Readonly<URLSearchParams>
): UnlayerEditorNavigationParams {
  return {
    origin: parseEditorOrigin(searchParams),
    entryRoute: parseEntryRouteContext(searchParams),
    projectId: searchParams.get('projectId'),
    projectName: searchParams.get('projectName'),
    linkedContactId: searchParams.get('linkedContactId'),
    linkedContactName: searchParams.get('linkedContactName'),
    linkedContactEmail: searchParams.get('linkedContactEmail'),
    contactId: searchParams.get('contactId'),
    contactName: searchParams.get('contactName'),
    returnTo: safeReturnToParam(searchParams.get('returnTo'), '/documents'),
    campaignContext: searchParams.get('campaignContext') === 'true',
    emailTemplateContext: searchParams.get('emailTemplateContext') === 'true',
    proposalContext: searchParams.get('proposalContext') === 'true',
    packagesBlockContext: searchParams.get('packagesBlockContext') === 'true',
    invoiceBlockContext: searchParams.get('invoiceBlockContext') === 'true',
    resendParam: searchParams.get('resend') === 'true',
    sendParam: searchParams.get('send') === 'true',
    resendContactId: searchParams.get('resendContactId'),
    sendContactId: searchParams.get('sendContactId'),
    shareStep: searchParams.get('step') === 'share',
  };
}

export function applyEditorEntryRouteContext(
  params: URLSearchParams,
  entryRoute: { kind: UnlayerEditorEntryRouteKind; path: string; stateTab?: number }
): URLSearchParams {
  params.set('entryRouteKind', entryRoute.kind);
  params.set('entryRoutePath', entryRoute.path);
  if (typeof entryRoute.stateTab === 'number') {
    params.set('entryRouteStateTab', String(entryRoute.stateTab));
  } else {
    params.delete('entryRouteStateTab');
  }
  return params;
}

export function clearResendFlowParams(
  searchParams: URLSearchParams | Readonly<URLSearchParams>
): URLSearchParams {
  const params = cloneParams(searchParams);
  params.delete('resend');
  params.delete('resendContactId');
  return params;
}

export function clearSendFlowParams(
  searchParams: URLSearchParams | Readonly<URLSearchParams>
): URLSearchParams {
  const params = cloneParams(searchParams);
  params.delete('send');
  params.delete('sendContactId');
  return params;
}

export function withShareStepParam(
  searchParams: URLSearchParams | Readonly<URLSearchParams>
): URLSearchParams {
  const params = cloneParams(searchParams);
  params.set('step', 'share');
  return params;
}

/** Window flag read by `useUnlayerNavigationGuard` to allow one popstate through. */
const UNLAYER_SKIP_NAVIGATION_BLOCK_KEY = '__unlayerSkipNavigationBlock';

function skipNavigationBlockWindow(): Record<string, unknown> {
  return window as unknown as Record<string, unknown>;
}

export function setUnlayerSkipNavigationBlock(): void {
  skipNavigationBlockWindow()[UNLAYER_SKIP_NAVIGATION_BLOCK_KEY] = true;
}

export function clearUnlayerSkipNavigationBlock(): void {
  delete skipNavigationBlockWindow()[UNLAYER_SKIP_NAVIGATION_BLOCK_KEY];
}

export function isUnlayerSkipNavigationBlockSet(): boolean {
  return Boolean(skipNavigationBlockWindow()[UNLAYER_SKIP_NAVIGATION_BLOCK_KEY]);
}

/** Remove `step=share` from a path+search(+hash) href so dismiss/back cannot stay on share. */
export function stripShareStepFromHref(href: string): string {
  const hashIndex = href.indexOf('#');
  const hash = hashIndex === -1 ? '' : href.slice(hashIndex);
  const withoutHash = hashIndex === -1 ? href : href.slice(0, hashIndex);
  const qIndex = withoutHash.indexOf('?');
  if (qIndex === -1) return href;
  const path = withoutHash.slice(0, qIndex);
  const params = new URLSearchParams(withoutHash.slice(qIndex + 1));
  if (params.get('step') !== 'share') return href;
  params.delete('step');
  const query = params.toString();
  return query ? `${path}?${query}${hash}` : `${path}${hash}`;
}

export function withTemplateCopyContextParams(
  searchParams: URLSearchParams | Readonly<URLSearchParams>,
  options: { kind: 'campaign' | 'template'; documentId: string }
): URLSearchParams {
  const params = cloneParams(searchParams);
  if (options.kind === 'campaign') {
    params.set(
      'returnTo',
      `/emails/create?emailKind=campaign&step=creation&documentId=${options.documentId}`
    );
    params.set('campaignContext', 'true');
  } else {
    params.set(
      'returnTo',
      `/emails/create?emailKind=template&step=subject&documentId=${options.documentId}`
    );
    params.set('emailTemplateContext', 'true');
  }
  return params;
}

/**
 * Marks the editor URL as a proposal for onboarding/Flows detection.
 * Call sites must use this helper — do not inline `proposalContext=true`.
 */
export function withProposalContextParam(
  searchParams: URLSearchParams | Readonly<URLSearchParams>
): URLSearchParams {
  const params = cloneParams(searchParams);
  params.set('proposalContext', 'true');
  return params;
}

/**
 * Marks the editor URL as a brochure for the Packages-block explainer (Flows).
 * Call sites must use this helper — do not inline `packagesBlockContext=true`.
 */
export function withPackagesBlockContextParam(
  searchParams: URLSearchParams | Readonly<URLSearchParams>
): URLSearchParams {
  const params = cloneParams(searchParams);
  params.set('packagesBlockContext', 'true');
  return params;
}

/**
 * Marks the editor URL as an invoice for the Invoice-block explainer (Flows).
 * Call sites must use this helper — do not inline `invoiceBlockContext=true`.
 */
export function withInvoiceBlockContextParam(
  searchParams: URLSearchParams | Readonly<URLSearchParams>
): URLSearchParams {
  const params = cloneParams(searchParams);
  params.set('invoiceBlockContext', 'true');
  return params;
}

export interface BuildEditorUrlWithParamsOptions {
  readonly pathname?: string;
}

export function buildEditorUrlWithParams(
  documentId: string,
  params: URLSearchParams,
  options?: BuildEditorUrlWithParamsOptions
): string {
  const query = params.toString();
  const basePath = isAdminTemplateEditorRoute(options?.pathname ?? '')
    ? buildAdminTemplateEditorPath(documentId)
    : `/documents/editor/${documentId}`;
  return query ? `${basePath}?${query}` : basePath;
}

export function cloneEditorParams(
  searchParams: URLSearchParams | Readonly<URLSearchParams>
): URLSearchParams {
  return cloneParams(searchParams);
}

/** Replace current editor URL after stripping resend flow params (avoids duplicate cleanup blocks). */
export function replaceEditorUrlStrippingResendParams(
  navigate: NavigateFunction,
  documentId: string,
  searchParams: URLSearchParams | Readonly<URLSearchParams>
): void {
  navigate(buildEditorUrlWithParams(documentId, clearResendFlowParams(searchParams)), {
    replace: true,
  });
}

/** Replace current editor URL after stripping send flow params. */
export function replaceEditorUrlStrippingSendParams(
  navigate: NavigateFunction,
  documentId: string,
  searchParams: URLSearchParams | Readonly<URLSearchParams>
): void {
  navigate(buildEditorUrlWithParams(documentId, clearSendFlowParams(searchParams)), {
    replace: true,
  });
}
